import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react"
import { addMonths, format, subMonths } from "date-fns"
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
} from "lucide-react"
import type React from "react"
import { useState, useRef, useEffect } from "react"
import { useRestaurantMaster } from "../../store/restaurant/reducer"
import { useSlotsMaster } from "../../store/slots/reducer"
import SlotDetailsModal from "../modals/SlotDetailsModal"

interface Props {
  query: string
}

const SlotManagementTable: React.FC<Props> = ({ query }) => {
  const toast = useToast()
  const { masterSlots } = useSlotsMaster()
  const { masterRestaurant, busy } = useRestaurantMaster()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedRestaurants, setSelectedRestaurants] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<any>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handlePreviousMonth = () => {
    setCurrentDate((prev) => subMonths(prev, 1))
  }

  const handleNextMonth = () => {
    setCurrentDate((prev) => addMonths(prev, 1))
  }
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure()

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate()

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1)

  const handleAddViewSlot = (day: number, existingSlot?: any) => {
    const formattedDate = format(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), day),
      "yyyy-MM-dd"
    )
    setSelectedDay(day)
    setSelectedSlot(existingSlot)
    console.log("Formatted Selected Date:", formattedDate)
    setIsModalOpen(true)
  }

  const handleRestaurantSelect = (id: string) => {
    setSelectedRestaurants((prev) =>
      prev.includes(id)
        ? prev.filter((restaurant) => restaurant !== id)
        : [...prev, id]
    )
  }

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
    if (!isDropdownOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
    }
  }

  // Filter restaurants based on search query
  const filteredRestaurants = masterRestaurant.filter((restaurant: any) =>
    restaurant.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
        setSearchQuery("")
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isDropdownOpen])

  console.log("selectedDay", selectedDay)

  return (
    <>
      <div
        style={{
          width: "96vw",
          maxWidth: "96vw",
          maxHeight: "86vh",
          height: "86vh",
          top: "8vh ",
          left: "4vw",
          overflowY: "auto",
          position: "relative",
          backgroundColor: "#EEEEEE",
          margin: "0 auto",
          padding: "30px",
        }}
      >
        <Box p={6}>
          <Flex justifyContent="space-between" alignItems="center" mb={6}>
            <Flex alignItems="center">
              <Button onClick={handlePreviousMonth} mr={2} variant="none">
                <ChevronLeftIcon />
              </Button>
              <Heading size="md">{format(currentDate, "MMMM yyyy")}</Heading>
              <Button onClick={handleNextMonth} ml={2} variant="none">
                <ChevronRightIcon />
              </Button>
            </Flex>

            {/* Custom Dropdown */}
            <Box position="relative" ref={dropdownRef}>
              <Button
                onClick={toggleDropdown}
                bg="white"
                width="260px"
                _hover={{ bg: "white" }}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={4}
              >
                <Text>
                  {selectedRestaurants.length > 0
                    ? `${selectedRestaurants.length} Selected`
                    : "Select Restaurants"}
                </Text>
                <ChevronDownIcon
                  style={{
                    transform: isDropdownOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </Button>

              {isDropdownOpen && (
                <Box
                  position="absolute"
                  top="calc(100% + 4px)"
                  left={0}
                  width="260px"
                  bg="white"
                  boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                  borderRadius="md"
                  zIndex={1000}
                  maxH="300px"
                  overflowY="auto"
                >
                  <Box
                    p={2}
                    position="sticky"
                    top={0}
                    bg="white"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    zIndex={1}
                  >
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon size={16} />
                      </InputLeftElement>
                      <Input
                        ref={searchInputRef}
                        placeholder="Search restaurants..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.300"
                        _focus={{
                          outline: "none",
                          borderColor: "blue.500",
                          boxShadow: "0 0 0 1px #3182ce",
                        }}
                      />
                    </InputGroup>
                  </Box>

                  <Box>
                    {filteredRestaurants.length > 0 ? (
                      filteredRestaurants.map((restaurant: any) => (
                        <Flex
                          key={restaurant._id}
                          alignItems="center"
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: "gray.100" }}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            handleRestaurantSelect(restaurant._id)
                            searchInputRef.current?.focus()
                          }}
                        >
                          <Checkbox
                            isChecked={selectedRestaurants.includes(
                              restaurant._id
                            )}
                            mr={2}
                            pointerEvents="none"
                          />
                          <Text fontSize="sm">{restaurant.name}</Text>
                        </Flex>
                      ))
                    ) : (
                      <Box px={3} py={2}>
                        <Text fontSize="sm" color="gray.500">
                          No restaurants found
                        </Text>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Flex>

          <Grid
            templateColumns="repeat(7, 1fr)"
            gap={0}
            borderRadius={8}
            bg="white"
          >
            {[
              "Sunday",
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
            ].map((day) => (
              <Box
                key={day}
                textAlign="center"
                fontWeight="semibold"
                p={2}
                bg="#e2444133"
              >
                {day}
              </Box>
            ))}

            {Array(
              new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                1
              ).getDay()
            )
              .fill(null)
              .map((_, index) => (
                <Box key={`empty-${index}`} p={2} />
              ))}

            {days.map((day) => (
              <Box
                key={day}
                borderWidth={"0.9px"}
                p={2}
                minH="100px"
                position="relative"
              >
                <Text fontSize="sm" mb={2}>
                  {day}{" "}
                  {format(
                    new Date(
                      currentDate.getFullYear(),
                      currentDate.getMonth(),
                      day
                    ),
                    "MMM"
                  )}
                </Text>
                <Button
                  variant="link"
                  color="red.500"
                  _hover={{ color: "red.600" }}
                  fontWeight="normal"
                  p={0}
                  height="auto"
                  onClick={(e) => {
                    e.preventDefault()
                    if (!selectedRestaurants.length) {
                      toast({
                        title: "Info",
                        description: "Please select a restaurant first.",
                        status: "info",
                        duration: 3000,
                        isClosable: true,
                        position: "top-right",
                      })
                    } else {
                      handleAddViewSlot(day)
                      onViewOpen()
                    }
                  }}
                >
                  Add/View Slot
                </Button>
              </Box>
            ))}
          </Grid>

          {isViewOpen && (
            <SlotDetailsModal
              isOpen={isViewOpen}
              onClose={onViewClose}
              restaurantIds={selectedRestaurants}
              selectedDay={
                selectedDay
                  ? format(
                      new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        selectedDay
                      ),
                      "yyyy-MM-dd"
                    )
                  : undefined
              }
            />
          )}
        </Box>
      </div>
    </>
  )
}

export default SlotManagementTable
